import React, { useEffect, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components/macro';
import {
  ClinicPetWhereInput,
  QueryMode,
  SearchClinicPetParentInput,
  useSidePanelSearchLazyQuery,
} from 'shared/types/graphql';
import { cleanPhoneNumber } from 'shared/utils';
import PetList from 'shared/components/SidePanel/components/SearchPanel/shared/components/PetList';
import PetParentList from 'shared/components/SidePanel/components/SearchPanel/shared/components/PetParentList';
import { useSidePanelSearch } from './state/providers/SearchPanelProvider';
import AddClientPatientForm from '../../shared/components/AddClientPatientForm';
import SidePanelPetParentProfile from './components/PetParentProfile';
import SidePanelHeader from './shared/components/SidePanelHeader';
import SidePanelPetProfile from './components/PetProfile';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import Tooltip from 'shared/components/Tooltip';
import { PopoverPlacement } from '../../../Popover';
import useDebounce from 'shared/hooks/useDebounce';
import ClientSearchResult from '../../shared/components/ClientSearchResult';
import { PanelViews } from '../../shared/types/enums/PanelViews';
import useClinicUser from 'shared/hooks/useClinicUser';
import { Mode } from './shared/types/enum/Mode';
import useAddChannelMember from 'shared/hooks/useAddChannelMember';
import SidePanelBenefits from './components/Benefits';
import { IPetSearchResult } from './shared/types/interfaces/IPetSearchResult';
import { RewardsPanel } from './components/LoyaltyProgram/components/RewardsPanel';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { Icon, VStack, Link, TextInput, Button, Collapse } from '@televet/kibble-ui';
import { useSidePanel } from '../../state/providers/SidePanelProvider';

const petSearchPageSize = 20;

const SidePanelSearch = (): JSX.Element => {
  const {
    isSearching,
    setIsSearching,
    petNameSearch,
    setPetNameSearch,
    petParentNameSearch,
    setPetParentNameSearch,
    phoneNumberSearch,
    setPhoneNumberSearch,
    currentView,
    currentClinicPetParentId,
    currentClinicPetId,
    selectedClient,
    selectedPatient,
    isSearchCollapsed,
    setIsSearchCollapsed,
    searchPanelMode,
    viewSearch,
    viewClinicPetParent,
    viewClinicPet,
    setSearchPanelMode,
    viewAddClientOrPatient,
    isShowInactivePetsEnabled,
    conversationId,
    onAddClientClickOverride,
  } = useSidePanelSearch();
  const { setIsOpen } = useSidePanel();
  const { currentClinicId } = useClinicUser();

  const [
    getSearchResults,
    {
      data: clinicPetParentsData,
      called: sidePanelSearchCalled,
      loading: isGettingClinicPetParentsData,
      refetch: refetchSearchResults,
    },
  ] = useSidePanelSearchLazyQuery({
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
  });

  const [petParentNameSearchDeb] = useDebounce(petParentNameSearch, 350);
  const [petNameSearchDeb] = useDebounce(petNameSearch, 350);
  const [phoneNumberSearchDeb] = useDebounce(phoneNumberSearch, 350);
  const [addChannelMember] = useAddChannelMember();
  const [isSyncing, setSyncingState] = useState(false);

  useEffect(() => {
    if (!isSearching) return;

    if (petParentNameSearchDeb === '' && petNameSearchDeb === '' && phoneNumberSearchDeb === '') return;

    const whereInput: SearchClinicPetParentInput = {
      and: [
        { phoneNumber: phoneNumberSearchDeb },
        { or: [{ fullName: petParentNameSearchDeb }, { pimsId: petParentNameSearchDeb }] },
        { or: [{ petNames: petNameSearchDeb }, { petPimsIds: petNameSearchDeb }] },
      ],
    };
    let petNameFilter: ClinicPetWhereInput = {};
    if (petNameSearchDeb) {
      const split = petNameSearchDeb.split(' ').map((s) => s.trim()).filter((s) => s);
      if (split.length > 1) {
        petNameFilter = {
          OR: [
            { AND: split.map((s) => ({ name: { contains: s, mode: QueryMode.Insensitive } })) },
            { AND: split.map((s) => ({ pimsId: { contains: s, mode: QueryMode.Insensitive } })) },
          ],
        };
      } else {
        petNameFilter = {
          OR: [
            { name: { contains: petNameSearchDeb, mode: QueryMode.Insensitive } },
            { pimsId: { contains: petNameSearchDeb, mode: QueryMode.Insensitive } },
          ],
        };
      }
    }
    const petWhere: ClinicPetWhereInput = {
      clinicId: { equals: currentClinicId || '' },
      isDeleted: { equals: false },
      ...petNameFilter,
      ...(!isShowInactivePetsEnabled
        ? {
            isActive: { equals: true },
            isDeceased: { not: { equals: true } },
          }
        : {}),
    };
    getSearchResults({
      variables: {
        whereInput,
        petWhere,
        pageSize: petSearchPageSize,
      },
    });
  }, [
    petNameSearchDeb,
    petParentNameSearchDeb,
    phoneNumberSearchDeb,
    getSearchResults,
    isSearching,
    isSyncing,
    currentClinicId,
    isShowInactivePetsEnabled,
  ]);

  const { primaryIntegrationName, isTriggerPimsSyncSupported, isClientIdSupported, isPatientIdSupported } =
    useIntegrationsProvider();

  // Reset search view when focused
  const onSearchInputFocus = (): void => {
    if (isSearching) return;
    viewSearch();
    setIsSearching(true);
  };

  const petParentResults = useMemo(() => {
    if (!clinicPetParentsData?.clinicPetParentSearch) return [];
    if (petNameSearch === '' && petParentNameSearch === '' && phoneNumberSearch === '') return [];

    return clinicPetParentsData?.clinicPetParentSearch;
  }, [clinicPetParentsData, petNameSearch, petParentNameSearch, phoneNumberSearch]);

  const petResults = useMemo(() => {
    if (!clinicPetParentsData?.clinicPetParentSearch) return [];
    if (petNameSearch === '' && petParentNameSearch === '' && phoneNumberSearch === '') return [];
    const petMap: Record<string, IPetSearchResult> = petParentResults.reduce((prev, clinicPetParent) => {
      clinicPetParent.pets.forEach((clinicPet) => {
        // TODO: this should be handled in the query itself, but the query where input does not currently
        // support either of these computed values
        if (!isShowInactivePetsEnabled && (!clinicPet.computedIsActive || clinicPet.computedIsDeceased)) return;
        if (prev[clinicPet.id]) {
          prev[clinicPet.id].petParents = prev[clinicPet.id].petParents.concat(clinicPetParent);
        } else {
          prev[clinicPet.id] = { ...clinicPet, petParents: [clinicPetParent] };
        }
      });
      return prev;
    }, {} as Record<string, IPetSearchResult>);
    return Object.values(petMap);
  }, [
    petParentResults,
    clinicPetParentsData,
    petNameSearch,
    petParentNameSearch,
    phoneNumberSearch,
    isShowInactivePetsEnabled,
  ]);

  const goToPetParent = useCallback(
    async (clinicPetParentId: string | undefined) => {
      if (!clinicPetParentId) return;
      viewClinicPetParent({ clinicPetParentId: clinicPetParentId });
    },
    [viewClinicPetParent],
  );

  const goToPet = useCallback(
    async (clinicPetId: string | undefined) => {
      if (!clinicPetId) return;
      viewClinicPet({ clinicPetId: clinicPetId });
    },
    [viewClinicPet],
  );

  /**
   * Add a pet parent to the channel provided
   */
  const addUserToChannel = useCallback(
    async (clinicPetParentId: string) => {
      if (!conversationId) {
        throw new Error('No channel id available');
      }
      await addChannelMember({ variables: { channelId: conversationId, clinicPetParentId } });
    },
    [addChannelMember, conversationId],
  );

  // Determine if clinic has PimsSync enabled
  const [triggerSync, setTriggerSyncState] = useState(false);

  const shouldShowPimsSync = isTriggerPimsSyncSupported;
  const clientSearchResultProps = {
    messageText: shouldShowPimsSync ? '' : "Not finding the result you're looking for?",
    callToActionText: `${shouldShowPimsSync ? 'a' : 'A'}dd a new client`,
  };
  const syncEnabledProps = shouldShowPimsSync
    ? {
        triggerSearch: (): void => setSyncingState(!isSyncing),
        secondaryCallToAction: (
          <Text size="sm">
            Try{' '}
            <Link
              size="sm"
              onClick={(): void => {
                setTriggerSyncState(true);
              }}
            >
              refreshing
            </Link>{' '}
            to get new clients from {primaryIntegrationName} or{' '}
          </Text>
        ),
        shouldSync: triggerSync,
        resetSyncState: (): void => setTriggerSyncState(false),
      }
    : {};

  return (
    <>
      {currentView !== PanelViews.AddClientPatientForm && (
        <SearchToolbar>
          <>
            <VStack align="stretch">
              <TextInput
                value={petParentNameSearch}
                placeholder={isClientIdSupported ? 'Search by client name or ID' : 'Search by client name'}
                name="search-pet-parent-name"
                autoFocus={currentView === PanelViews.Search}
                onChange={(e: { target: { value: string } }): void => {
                  setPetParentNameSearch(e.target.value);
                }}
                onFocus={onSearchInputFocus}
                data-mixpanel-name="Search by client name"
                data-testid="search-pet-parent-name"
                rightElement={
                  <Tooltip
                    content={isSearchCollapsed ? 'Show additional search options' : 'Hide additional search options'}
                    placement={PopoverPlacement.TopEnd}
                  >
                    <Button
                      data-testid="expand-parent-search-button"
                      iconName={isSearchCollapsed ? 'chevronDown' : 'chevronUp'}
                      onClick={(): void => setIsSearchCollapsed(!isSearchCollapsed)}
                      size="sm"
                      variant="ghostNeutral"
                    />
                  </Tooltip>
                }
              />
              <Collapse in={!isSearchCollapsed}>
                <VStack p="1px">
                  <TextInput
                    value={petNameSearch}
                    placeholder={isPatientIdSupported ? 'Search by patient name or ID' : 'Search by patient name'}
                    name="search-pet-name"
                    onChange={(e: { target: { value: string } }): void => {
                      setPetNameSearch(e.target.value);
                    }}
                    onFocus={onSearchInputFocus}
                    data-mixpanel-name="Search by patient name"
                    data-testid="search-pet-name"
                  />
                  <TextInput
                    value={phoneNumberSearch}
                    style={{ marginBottom: 'none' }}
                    placeholder="Search by client phone number"
                    name="search-pet-parent-phone-number"
                    onChange={(e: { target: { value: string } }): void => {
                      setPhoneNumberSearch(cleanPhoneNumber(e.target.value));
                    }}
                    onFocus={onSearchInputFocus}
                    data-mixpanel-name="Search by phone number"
                    data-testid="search-phone-number"
                  />
                </VStack>
              </Collapse>
            </VStack>
            {isSearching && sidePanelSearchCalled && (
              <ClientSearchResult
                icon={<Icon name="personAdd" />}
                callToActionFunction={viewAddClientOrPatient}
                {...clientSearchResultProps}
                mixpanelAttribute="Add new client from search results button"
                backgroundColor="background.alpha"
                shouldShowPimsSync={shouldShowPimsSync}
                {...syncEnabledProps}
              />
            )}
          </>
        </SearchToolbar>
      )}
      {currentView === PanelViews.Search && sidePanelSearchCalled && (
        <VStack
          className="SearchPanel__SearchResults"
          align="stretch"
          justify="flex-start"
          bg="background.subtle"
          spacing={0}
          mt={0}
          height="100%"
          overflow="auto"
        >
          {searchPanelMode !== Mode.LinkToChannel && searchPanelMode !== Mode.LinkToPet && (
            <PetList
              petList={petResults}
              goToPet={goToPet}
              goToPetParent={goToPetParent}
              isGettingClinicPetParentsData={isGettingClinicPetParentsData}
              refetchSearchResults={refetchSearchResults}
            />
          )}
          <PetParentList
            petParentList={petParentResults}
            isGettingClinicPetParentsData={isGettingClinicPetParentsData}
          />
        </VStack>
      )}

      {currentView === PanelViews.AddClientPatientForm && (
        <AddClientPatientForm
          goToPetParent={goToPetParent}
          goToPet={goToPet}
          onAddClientClickOverride={onAddClientClickOverride}
          addUserToChannel={addUserToChannel}
          searchPanelMode={searchPanelMode}
          onBackButtonClick={(): void => {
            if (selectedClient) {
              viewClinicPetParent({ clinicPetParentId: selectedClient.id });
            } else if (selectedPatient) {
              viewClinicPet({ clinicPetId: selectedPatient.id });
            } else {
              viewSearch();
            }
            setSearchPanelMode(Mode.Default);
          }}
          closePanel={(): void => setIsOpen(false)}
        />
      )}

      {currentView === PanelViews.PetProfile && (
        <>
          <SidePanelHeader />
          <SidePanelPetProfile clinicPetId={currentClinicPetId} searchPanelMode={searchPanelMode} />
        </>
      )}
      {currentView === PanelViews.PetParentProfile && (
        <>
          <SidePanelHeader />
          <SidePanelPetParentProfile
            clinicPetParentId={currentClinicPetParentId}
            goToPetParent={goToPetParent}
            addUserToChannel={addUserToChannel}
            isValidChannel={!!conversationId}
          />
        </>
      )}
      {currentView === PanelViews.LoyaltyRewards && (
        <>
          <SidePanelHeader />
          <RewardsPanel />
        </>
      )}
      {currentView === PanelViews.Benefits && (
        <>
          <SidePanelHeader />
          <SidePanelBenefits clinicPetParentId={currentClinicPetParentId} key={currentClinicPetId} />
        </>
      )}
    </>
  );
};

export default SidePanelSearch;

const SearchToolbar = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  padding: 16px;
  border-bottom: 1px solid #e8e9e9;
`;
